import { useState, useEffect, useCallback } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { ApproachVersion } from 'types/approach';
import { getApproachVersions } from '../api/marketplaceApi';

export interface UseApproachVersionsResult {
  versions: ApproachVersion[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApproachVersions(
  systemName: string | null
): UseApproachVersionsResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [versions, setVersions] = useState<ApproachVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!apiClients || !organizationId || !systemName) {
      setVersions([]);
      return;
    }

    let cancelled = false;

    const fetchVersions = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getApproachVersions(
          apiClients.schemaClient,
          systemName
        );

        if (!cancelled) {
          setVersions(response.versions);
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to load approach versions';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchVersions();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, systemName, fetchTrigger]);

  return { versions, loading, error, refetch };
}
